import Vue from "vue";
import { Mixin } from "vue-mixin-decorator";
import { Config, courseImage, course } from "@/shims-configs";
import { getState, getCourses, COURSE_APPLY_STATE } from "@/apis";
import {
  getQueryParamValue,
  URL_PARAM_SERIAL_CODE,
  URL_PARAM_TOKEN,
  URL_PARAM_STATE,
  URL_PARAM_REDIRECT_CODE,
  URL_PARAM_ERROR,
  URL_PARAM_TRAN_SRC,
} from "@/utils/url";
import configs from "@/configs";

@Mixin
export default class SettingMixin extends Vue {
  errorPopupImage = "";
  errorPopupButton = "";
  // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/explicit-module-boundary-types
  errorPopupFunction = () => {};

  async mounted(): Promise<void> {
    document.title = this.config.title;
  }

  get serialCode(): string {
    return this.$route.query[URL_PARAM_SERIAL_CODE] as string;
  }

  get serialCodeQueryParamValue(): string {
    return getQueryParamValue(URL_PARAM_SERIAL_CODE);
  }

  get token(): string {
    return this.$route.query[URL_PARAM_TOKEN] as string;
  }

  get tokenQueryParamValue(): string {
    return getQueryParamValue(URL_PARAM_TOKEN);
  }

  get campaignId(): string {
    return this.config.id;
  }

  get config(): Config {
    return configs;
  }

  get courseId(): number {
    const course = this.$route.query.course;
    if (!course) return -1;
    return parseInt(String(this.$route.query.course));
  }

  get errorCode(): string {
    return getQueryParamValue(URL_PARAM_ERROR);
  }

  get state(): string {
    return getQueryParamValue(URL_PARAM_STATE);
  }

  get redirectCode(): string {
    return this.$route.query[URL_PARAM_REDIRECT_CODE] as string;
  }

  get redirectCodeQueryParamValue(): string {
    return getQueryParamValue(URL_PARAM_REDIRECT_CODE);
  }

  get transitionSrc(): string {
    return (this.$route.query[URL_PARAM_TRAN_SRC] as string) ?? "";
  }

  get transitionSrcQueryParamValue(): string {
    return getQueryParamValue(URL_PARAM_TRAN_SRC);
  }

  get bannerMain(): string {
    return this.config.imageName.header?.bannerMain
      ? `./images/campaign/${this.config.imageName.header.bannerMain}`
      : "";
  }

  get information(): string {
    return this.config.imageName.footer?.information
      ? `./images/campaign/${this.config.imageName.footer.information}`
      : "";
  }
  get faq(): string {
    return this.config.imageName.footer?.faq
      ? `./images/campaign/${this.config.imageName.footer.faq}`
      : "";
  }
  get showFaq(): string {
    return this.config.imageName.footer?.showFaq
      ? `./images/campaign/${this.config.imageName.footer.showFaq}`
      : "";
  }
  get hideFaq(): string {
    return this.config.imageName.footer?.hideFaq
      ? `./images/campaign/${this.config.imageName.footer.hideFaq}`
      : "";
  }
  get requirements(): string {
    return this.config.imageName.footer?.requirements
      ? `./images/campaign/${this.config.imageName.footer.requirements}`
      : "";
  }
  get showRequirements(): string {
    return this.config.imageName.footer?.showRequirements
      ? `./images/campaign/${this.config.imageName.footer.showRequirements}`
      : "";
  }
  get hideRequirements(): string {
    return this.config.imageName.footer?.hideRequirements
      ? `./images/campaign/${this.config.imageName.footer.hideRequirements}`
      : "";
  }

  get backgroundColor(): string {
    return this.config.backgroundColor;
  }
  get fontColor(): string {
    return this.config.textColor;
  }
  get configStyle(): string {
    return `background: ${this.backgroundColor}; color: ${this.fontColor};`;
  }

  get courseGroupEnv(): { [type: string]: course } {
    if (window.location.host.indexOf("localhost") != -1)
      return this.config.courseGroups.dev;
    if (window.location.host.indexOf("dev") != -1)
      return this.config.courseGroups.dev;
    if (window.location.host.indexOf("stg") != -1)
      return this.config.courseGroups.stg;
    return this.config.courseGroups.prd;
  }

  get courseImageNameEnv(): { [type: string]: courseImage } {
    if (window.location.host.indexOf("localhost") != -1)
      return this.config.imageName.courses.dev;
    if (window.location.host.indexOf("dev") != -1)
      return this.config.imageName.courses.dev;
    if (window.location.host.indexOf("stg") != -1)
      return this.config.imageName.courses.stg;
    return this.config.imageName.courses.prd;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get refs(): any {
    return this.$refs;
  }

  async checkCampaignStatus(): Promise<boolean> {
    try {
      const res = await getState(this.campaignId);
      // 応募画面 期間外画面
      if (res.data.state === "teaser") {
        this.routerPushExpiredPage("teaser");
        return false;
      } else if (res.data.state === "closed") {
        this.routerPushExpiredPage("closed");
        return false;
      } else if (res.data.state === "maintenance") {
        this.routerPushExpiredPage("teaser");
        return false;
      }
      return true;
    } catch {
      this.routerPushSystemErrorPage();
      return false;
    }
  }

  async checkIsWin(): Promise<boolean> {
    try {
      // 応募コース取得
      const res = await getCourses(this.campaignId, this.token);

      const applyNg = res.data.course_groups.find(
        (x) => x.course_group_apply_state !== COURSE_APPLY_STATE.ApplyOk
      );

      const isWin = !!applyNg;
      if (isWin) {
        this.routerPushChargeMyPage();
        return true;
      }
      return false;
    } catch {
      this.routerPushSystemErrorPage();
      return false;
    }
  }

  pushWithTransition(push: () => void): void {
    this.refs.overlay.showOverlay(() => {
      push();
    });
  }

  routerPushCampaignPage(serialCode = ""): void {
    this.$router.push({
      name: "Campaign",
      query: {
        [URL_PARAM_TOKEN]: this.token,
        [URL_PARAM_SERIAL_CODE]: serialCode,
        [URL_PARAM_TRAN_SRC]: this.transitionSrc,
      },
    });
  }

  routerPushExpiredPage(state: string): void {
    this.$router.push({
      name: "Expired",
      query: {
        [URL_PARAM_TOKEN]: this.token,
        [URL_PARAM_STATE]: state,
        [URL_PARAM_TRAN_SRC]: this.transitionSrc,
      },
    });
  }

  routerPushAddFriendPage(): void {
    this.$router.push({
      name: "AddFriend",
      query: {
        [URL_PARAM_TOKEN]: this.token,
        [URL_PARAM_TRAN_SRC]: this.transitionSrc,
      },
    });
  }

  routerPushChargeMyPage(): void {
    this.$router.push({
      name: "MyPage",
      query: {
        [URL_PARAM_SERIAL_CODE]: this.serialCode,
        [URL_PARAM_REDIRECT_CODE]: this.redirectCode,
        [URL_PARAM_TOKEN]: this.token,
        [URL_PARAM_TRAN_SRC]: this.transitionSrc,
      },
    });
  }

  routerPushRequestHistoryPage(): void {
    this.$router.push({
      name: "RequestHistory",
      query: {
        [URL_PARAM_TOKEN]: this.token,
        [URL_PARAM_TRAN_SRC]: this.transitionSrc,
      },
    });
  }

  routerPushCollectHistoryPage(): void {
    this.$router.push({
      name: "CollectHistory",
      query: {
        [URL_PARAM_TOKEN]: this.token,
        [URL_PARAM_TRAN_SRC]: this.transitionSrc,
      },
    });
  }

  routerPushRequestPage(): void {
    this.$router.push({
      name: "Request",
      query: {
        [URL_PARAM_TOKEN]: this.token,
        [URL_PARAM_TRAN_SRC]: this.transitionSrc,
      },
    });
  }

  routerPushCoursesPage(): void {
    this.$router.push({
      name: "Courses",
      query: {
        [URL_PARAM_TOKEN]: this.token,
        [URL_PARAM_TRAN_SRC]: this.transitionSrc,
      },
    });
  }

  routerPushCollabCoursesPage(): void {
    this.$router.push({
      name: "CollabCourses",
      query: {
        [URL_PARAM_SERIAL_CODE]: this.serialCode,
        [URL_PARAM_TOKEN]: this.token,
        [URL_PARAM_TRAN_SRC]: this.transitionSrc,
      },
    });
  }

  routerPushLineCoursesPage(): void {
    this.$router.push({
      name: "LineCourses",
      query: {
        [URL_PARAM_SERIAL_CODE]: this.serialCode,
        [URL_PARAM_TOKEN]: this.token,
        [URL_PARAM_TRAN_SRC]: this.transitionSrc,
      },
    });
  }

  routerPushCollectionHistoryPage(): void {
    this.$router.push({
      name: "CollectionHistory",
      query: {
        [URL_PARAM_TOKEN]: this.token,
        [URL_PARAM_TRAN_SRC]: this.transitionSrc,
      },
    });
  }

  routerPushSystemErrorPage(): void {
    this.$router.push({
      name: "SystemError",
      query: {
        [URL_PARAM_TOKEN]: this.token,
        [URL_PARAM_TRAN_SRC]: this.transitionSrc,
      },
    });
  }

  getImagePath(val: string): string {
    return `./images/campaign/${val}`;
  }

  // 各種エラー
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  responseError(err: any): {
    image: string;
    button: string;
    func?: () => void;
  } {
    // postActionLog(
    //   this.campaignId,
    //   this.token,
    //   this.serialCode,
    //   String(this.courseId),
    //   "error"
    // );

    const errorImages: {
      [n: number]: {
        image: string;
        button: string;
        func?: () => void;
      };
    } = {
      500: {
        image: String(this.config.imageName.errorPopup.systemError),
        button: String(this.config.imageName.errorPopup.popupClose),
        func: this.routerPushCampaignPage,
      },
      1007: {
        image: String(this.config.imageName.errorPopup.addFriend),
        button: String(this.config.imageName.errorPopup.goToTopPage),
        func: this.routerPushAddFriendPage,
      },
      2004: {
        image: String(this.config.imageName.errorPopup.addFriend),
        button: String(this.config.imageName.errorPopup.goToTopPage),
        func: this.routerPushCampaignPage,
      },
      1006: {
        image: String(this.config.imageName.errorPopup.invalidSerialCode),
        button: String(this.config.imageName.errorPopup.popupClose),
      },
      2002: {
        image: String(this.config.imageName.errorPopup.usedSerialCode),
        button: String(this.config.imageName.errorPopup.popupClose),
      },
      2003: {
        image: String(this.config.imageName.errorPopup.usedSerialCode),
        button: String(this.config.imageName.errorPopup.popupClose),
      },
      2024: {
        image: String(this.config.imageName.errorPopup.invalidPeriod),
        button: String(this.config.imageName.errorPopup.popupClose),
      },
      3006: {
        image: String(this.config.imageName.errorPopup.errorLinePoint),
        button: String(this.config.imageName.errorPopup.popupClose),
      },
    };

    // システムエラー ポップアップ
    if (err.response.status === 500) {
      return errorImages[500];
    }

    const code = err.response.data.code;

    const popupSetting = errorImages[code];
    if (popupSetting === undefined) {
      this.routerPushSystemErrorPage();
    }

    return popupSetting;
  }

  showErrorDialog(setting: {
    image: string;
    button: string;
    func?: () => void;
  }): void {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    this.errorPopupFunction = () => {};
    this.errorPopupImage = "";
    this.errorPopupButton = "";

    this.errorPopupImage = setting.image;
    this.errorPopupButton = setting.button;
    if (setting.func !== undefined) this.errorPopupFunction = setting.func;
    setTimeout(() => {
      this.refs.errorDialog.show();
    }, 500);
  }

  errorDialogClose(): void {
    this.errorPopupFunction();
  }
}
